import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import DOMPurify from "dompurify";
import axios from "axios";
import { 
  ArrowLeft, Clock, Check, Lock, Play, FileText, 
  ChevronRight, ChevronDown, Award, AlertCircle, 
  CheckCircle, XCircle, Loader2, BookOpen
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const translations = {
  en: {
    back: "Back to My Courses",
    module: "Module",
    lesson: "Lesson",
    progress: "Progress",
    time_spent: "Time Spent",
    time_required: "Time Required",
    minutes: "minutes",
    hours: "hours",
    complete_lesson: "Complete Lesson",
    locked: "Locked",
    unlock_message: "Complete the previous module to unlock",
    time_message: "Spend at least 3 hours on this module to unlock the quiz",
    quiz: "Module Quiz",
    start_quiz: "Start Quiz",
    quiz_locked: "Quiz Locked",
    quiz_passed: "Quiz Passed",
    quiz_failed: "Quiz Failed - Try Again",
    question: "Question",
    of: "of",
    submit_quiz: "Submit Quiz",
    next: "Next",
    previous: "Previous",
    final_exam: "Final Examination",
    final_exam_desc: "Complete all modules to unlock the final exam (30 questions, 100% required)",
    get_certificate: "Get Certificate",
    congratulations: "Congratulations!",
    course_completed: "You have completed this course!",
    loading: "Loading course...",
    error: "Error loading course",
    not_enrolled: "You are not enrolled in this course"
  },
  fr: {
    back: "Retour à Mes Cours",
    module: "Module",
    lesson: "Leçon",
    progress: "Progression",
    time_spent: "Temps Passé",
    time_required: "Temps Requis",
    minutes: "minutes",
    hours: "heures",
    complete_lesson: "Terminer la Leçon",
    locked: "Verrouillé",
    unlock_message: "Complétez le module précédent pour débloquer",
    time_message: "Passez au moins 3 heures sur ce module pour débloquer le quiz",
    quiz: "Quiz du Module",
    start_quiz: "Commencer le Quiz",
    quiz_locked: "Quiz Verrouillé",
    quiz_passed: "Quiz Réussi",
    quiz_failed: "Quiz Échoué - Réessayer",
    question: "Question",
    of: "sur",
    submit_quiz: "Soumettre le Quiz",
    next: "Suivant",
    previous: "Précédent",
    final_exam: "Examen Final",
    final_exam_desc: "Complétez tous les modules pour débloquer l'examen final (30 questions, 100% requis)",
    get_certificate: "Obtenir le Certificat",
    congratulations: "Félicitations !",
    course_completed: "Vous avez terminé ce cours !",
    loading: "Chargement du cours...",
    error: "Erreur de chargement du cours",
    not_enrolled: "Vous n'êtes pas inscrit à ce cours"
  },
  pt: {
    back: "Voltar aos Meus Cursos",
    module: "Módulo",
    lesson: "Lição",
    progress: "Progresso",
    time_spent: "Tempo Gasto",
    time_required: "Tempo Necessário",
    minutes: "minutos",
    hours: "horas",
    complete_lesson: "Completar Lição",
    locked: "Bloqueado",
    unlock_message: "Complete o módulo anterior para desbloquear",
    time_message: "Passe pelo menos 3 horas neste módulo para desbloquear o quiz",
    quiz: "Quiz do Módulo",
    start_quiz: "Iniciar Quiz",
    quiz_locked: "Quiz Bloqueado",
    quiz_passed: "Quiz Aprovado",
    quiz_failed: "Quiz Reprovado - Tentar Novamente",
    question: "Pergunta",
    of: "de",
    submit_quiz: "Enviar Quiz",
    next: "Próximo",
    previous: "Anterior",
    final_exam: "Exame Final",
    final_exam_desc: "Complete todos os módulos para desbloquear o exame final (30 questões, 100% necessário)",
    get_certificate: "Obter Certificado",
    congratulations: "Parabéns!",
    course_completed: "Você completou este curso!",
    loading: "Carregando curso...",
    error: "Erro ao carregar curso",
    not_enrolled: "Você não está inscrito neste curso"
  },
  es: {
    back: "Volver a Mis Cursos",
    module: "Módulo",
    lesson: "Lección",
    progress: "Progreso",
    time_spent: "Tiempo Transcurrido",
    time_required: "Tiempo Requerido",
    minutes: "minutos",
    hours: "horas",
    complete_lesson: "Completar Lección",
    locked: "Bloqueado",
    unlock_message: "Complete el módulo anterior para desbloquear",
    time_message: "Pase al menos 3 horas en este módulo para desbloquear el quiz",
    quiz: "Quiz del Módulo",
    start_quiz: "Iniciar Quiz",
    quiz_locked: "Quiz Bloqueado",
    quiz_passed: "Quiz Aprobado",
    quiz_failed: "Quiz Fallido - Intentar de Nuevo",
    question: "Pregunta",
    of: "de",
    submit_quiz: "Enviar Quiz",
    next: "Siguiente",
    previous: "Anterior",
    final_exam: "Examen Final",
    final_exam_desc: "Complete todos los módulos para desbloquear el examen final (30 preguntas, 100% requerido)",
    get_certificate: "Obtener Certificado",
    congratulations: "¡Felicidades!",
    course_completed: "¡Has completado este curso!",
    loading: "Cargando curso...",
    error: "Error al cargar curso",
    not_enrolled: "No estás inscrito en este curso"
  },
  de: {
    back: "Zurück zu Meinen Kursen",
    module: "Modul",
    lesson: "Lektion",
    progress: "Fortschritt",
    time_spent: "Verbrachte Zeit",
    time_required: "Erforderliche Zeit",
    minutes: "Minuten",
    hours: "Stunden",
    complete_lesson: "Lektion Abschließen",
    locked: "Gesperrt",
    unlock_message: "Schließen Sie das vorherige Modul ab, um freizuschalten",
    time_message: "Verbringen Sie mindestens 3 Stunden in diesem Modul, um das Quiz freizuschalten",
    quiz: "Modul-Quiz",
    start_quiz: "Quiz Starten",
    quiz_locked: "Quiz Gesperrt",
    quiz_passed: "Quiz Bestanden",
    quiz_failed: "Quiz Nicht Bestanden - Erneut Versuchen",
    question: "Frage",
    of: "von",
    submit_quiz: "Quiz Absenden",
    next: "Weiter",
    previous: "Zurück",
    final_exam: "Abschlussprüfung",
    final_exam_desc: "Schließen Sie alle Module ab, um die Abschlussprüfung freizuschalten (30 Fragen, 100% erforderlich)",
    get_certificate: "Zertifikat Erhalten",
    congratulations: "Herzlichen Glückwunsch!",
    course_completed: "Sie haben diesen Kurs abgeschlossen!",
    loading: "Kurs wird geladen...",
    error: "Fehler beim Laden des Kurses",
    not_enrolled: "Sie sind nicht in diesem Kurs eingeschrieben"
  }
};

const CourseViewerPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { isAuthenticated, user } = useAuth();
  const t = translations[language] || translations.en;
  
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});
  const [currentLesson, setCurrentLesson] = useState(null);
  const [showQuiz, setShowQuiz] = useState(false);
  const [currentQuiz, setCurrentQuiz] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  
  // Timer for tracking time spent on lessons
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchCourseData();
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, isAuthenticated, navigate]);

  // Track time spent when viewing a lesson
  useEffect(() => {
    if (currentLesson) {
      startTimeRef.current = Date.now();
      timerRef.current = setInterval(() => {
        updateTimeSpent();
      }, 60000); // Update every minute
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        updateTimeSpent(); // Final update when leaving lesson
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLesson]);

  const fetchCourseData = async () => {
    try {
      const [courseRes, progressRes] = await Promise.all([
        axios.get(`${API}/elearning/courses/${courseId}`, { withCredentials: true }),
        axios.get(`${API}/elearning/my-courses/${courseId}/progress`, { withCredentials: true })
      ]);
      
      setCourse(courseRes.data);
      setProgress(progressRes.data);
      
      // Auto-expand first incomplete module
      if (courseRes.data.modules) {
        const firstIncomplete = courseRes.data.modules.findIndex(
          (m, idx) => !progressRes.data.modules_progress?.[m.id]?.completed
        );
        if (firstIncomplete >= 0) {
          setExpandedModules({ [firstIncomplete]: true });
        }
      }
    } catch (err) {
      console.error('Error fetching course:', err);
      setError(err.response?.data?.detail || 'Error loading course');
    } finally {
      setLoading(false);
    }
  };

  const updateTimeSpent = async () => {
    if (!startTimeRef.current || !currentLesson) return;
    
    const timeSpent = Math.floor((Date.now() - startTimeRef.current) / 1000);
    if (timeSpent < 10) return; // Don't update for less than 10 seconds
    
    try {
      await axios.post(
        `${API}/elearning/my-courses/${courseId}/lessons/${currentLesson.id}/time`,
        { seconds: timeSpent },
        { withCredentials: true }
      );
    } catch (err) {
      console.error('Error updating time:', err);
    }
  };

  const completeLesson = async (lessonId) => {
    try {
      await axios.post(
        `${API}/elearning/my-courses/${courseId}/lessons/${lessonId}/complete`,
        {},
        { withCredentials: true }
      );
      fetchCourseData(); // Refresh progress
    } catch (err) {
      console.error('Error completing lesson:', err);
    }
  };

  const startQuiz = (moduleId) => {
    const module = course.modules.find(m => m.id === moduleId);
    if (module?.quiz) {
      setCurrentQuiz({ moduleId, questions: module.quiz.questions });
      setQuizAnswers({});
      setQuizResult(null);
      setShowQuiz(true);
    }
  };

  const submitQuiz = async () => {
    setSubmittingQuiz(true);
    try {
      const response = await axios.post(
        `${API}/elearning/my-courses/${courseId}/quiz/${currentQuiz.moduleId}/submit`,
        { answers: quizAnswers },
        { withCredentials: true }
      );
      setQuizResult(response.data);
      if (response.data.passed) {
        setTimeout(() => {
          setShowQuiz(false);
          fetchCourseData();
        }, 2000);
      }
    } catch (err) {
      console.error('Error submitting quiz:', err);
    } finally {
      setSubmittingQuiz(false);
    }
  };

  const getModuleProgress = (moduleId) => {
    return progress?.modules_progress?.[moduleId] || { 
      time_spent_seconds: 0, 
      lessons_completed: [], 
      quiz_passed: false 
    };
  };

  const isModuleLocked = (moduleIndex) => {
    if (moduleIndex === 0) return false;
    const prevModule = course.modules[moduleIndex - 1];
    return !getModuleProgress(prevModule.id).quiz_passed;
  };

  const isQuizUnlocked = (moduleId) => {
    const moduleProgress = getModuleProgress(moduleId);
    const requiredTime = 3 * 60 * 60; // 3 hours in seconds
    return moduleProgress.time_spent_seconds >= requiredTime;
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const getTitle = (item) => {
    return item[`title_${language}`] || item.title_en || item.title_fr || item.title || '';
  };

  const getDescription = (item) => {
    return item[`description_${language}`] || item.description_en || item.description_fr || item.description || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-kamit-gold mx-auto mb-4" />
          <p className="text-kamit-brown">{t.loading}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 to-amber-50 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-2xl shadow-lg">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-kamit-brown text-lg mb-4">{error}</p>
          <Link 
            to="/my-courses"
            className="inline-flex items-center gap-2 text-kamit-gold hover:text-kamit-brown transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            {t.back}
          </Link>
        </div>
      </div>
    );
  }

  // Quiz Modal
  if (showQuiz && currentQuiz) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 to-amber-50 py-12">
        <div className="max-w-3xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-heading text-kamit-brown">
                {t.quiz} - {t.module} {course.modules.findIndex(m => m.id === currentQuiz.moduleId) + 1}
              </h2>
              <button
                onClick={() => setShowQuiz(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>

            {quizResult ? (
              <div className={`text-center py-8 ${quizResult.passed ? 'text-green-600' : 'text-red-600'}`}>
                {quizResult.passed ? (
                  <>
                    <CheckCircle className="h-16 w-16 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold mb-2">{t.quiz_passed}</h3>
                    <p>Score: {quizResult.score}%</p>
                  </>
                ) : (
                  <>
                    <XCircle className="h-16 w-16 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold mb-2">{t.quiz_failed}</h3>
                    <p>Score: {quizResult.score}% (90% required)</p>
                    <button
                      onClick={() => {
                        setQuizAnswers({});
                        setQuizResult(null);
                      }}
                      className="mt-4 px-6 py-2 bg-kamit-gold text-white rounded-lg hover:bg-kamit-brown transition-colors"
                    >
                      {t.quiz_failed.split(' - ')[1]}
                    </button>
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                {currentQuiz.questions?.map((question, qIdx) => (
                  <div key={qIdx} className="border-b border-gray-200 pb-6">
                    <p className="font-medium text-kamit-brown mb-4">
                      {t.question} {qIdx + 1} {t.of} {currentQuiz.questions.length}: {question.question}
                    </p>
                    <div className="space-y-2">
                      {question.options?.map((option, oIdx) => (
                        <label
                          key={oIdx}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            quizAnswers[qIdx] === oIdx 
                              ? 'border-kamit-gold bg-kamit-gold/10' 
                              : 'border-gray-200 hover:border-kamit-gold/50'
                          }`}
                        >
                          <input
                            type="radio"
                            name={`question_${qIdx}`}
                            checked={quizAnswers[qIdx] === oIdx}
                            onChange={() => setQuizAnswers({ ...quizAnswers, [qIdx]: oIdx })}
                            className="sr-only"
                          />
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            quizAnswers[qIdx] === oIdx ? 'border-kamit-gold' : 'border-gray-300'
                          }`}>
                            {quizAnswers[qIdx] === oIdx && (
                              <div className="w-3 h-3 rounded-full bg-kamit-gold" />
                            )}
                          </div>
                          <span>{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}

                <button
                  onClick={submitQuiz}
                  disabled={submittingQuiz || Object.keys(quizAnswers).length !== currentQuiz.questions?.length}
                  className="w-full py-3 bg-kamit-gold text-white rounded-lg font-medium hover:bg-kamit-brown transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {submittingQuiz && <Loader2 className="h-5 w-5 animate-spin" />}
                  {t.submit_quiz}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-amber-50">
      {/* Header */}
      <div className="bg-kamit-brown text-white py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/my-courses"
            className="inline-flex items-center gap-2 text-kamit-gold hover:text-white transition-colors mb-4"
          >
            <ArrowLeft className="h-5 w-5" />
            {t.back}
          </Link>
          <h1 className="text-3xl font-heading">{getTitle(course)}</h1>
          <div className="flex items-center gap-4 mt-2 text-white/80">
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              {course.total_hours}h
            </span>
            <span className="flex items-center gap-1">
              <BookOpen className="h-4 w-4" />
              {course.modules?.length || 0} {t.module}s
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Sidebar - Module List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden sticky top-4">
              <div className="p-4 bg-kamit-gold text-white">
                <h2 className="font-heading text-lg">{t.progress}</h2>
                <div className="mt-2 h-2 bg-white/30 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-white transition-all duration-500"
                    style={{ width: `${progress?.overall_progress || 0}%` }}
                  />
                </div>
                <p className="mt-1 text-sm">{progress?.overall_progress || 0}%</p>
              </div>

              <div className="divide-y divide-gray-100">
                {course.modules?.map((module, idx) => {
                  const moduleProgress = getModuleProgress(module.id);
                  const isLocked = isModuleLocked(idx);
                  const quizUnlocked = isQuizUnlocked(module.id);
                  
                  return (
                    <div key={module.id} className="relative">
                      <button
                        onClick={() => !isLocked && setExpandedModules(prev => ({ 
                          ...prev, 
                          [idx]: !prev[idx] 
                        }))}
                        className={`w-full p-4 text-left flex items-center justify-between ${
                          isLocked ? 'bg-gray-50 cursor-not-allowed' : 'hover:bg-kamit-beige/30'
                        }`}
                        disabled={isLocked}
                      >
                        <div className="flex items-center gap-3">
                          {isLocked ? (
                            <Lock className="h-5 w-5 text-gray-400" />
                          ) : moduleProgress.quiz_passed ? (
                            <CheckCircle className="h-5 w-5 text-green-500" />
                          ) : (
                            <div className="w-5 h-5 rounded-full border-2 border-kamit-gold flex items-center justify-center text-xs font-bold text-kamit-gold">
                              {idx + 1}
                            </div>
                          )}
                          <div>
                            <p className={`font-medium ${isLocked ? 'text-gray-400' : 'text-kamit-brown'}`}>
                              {t.module} {idx + 1}
                            </p>
                            <p className={`text-sm ${isLocked ? 'text-gray-400' : 'text-gray-500'}`}>
                              {getTitle(module)}
                            </p>
                          </div>
                        </div>
                        {!isLocked && (
                          expandedModules[idx] ? (
                            <ChevronDown className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronRight className="h-5 w-5 text-gray-400" />
                          )
                        )}
                      </button>

                      {expandedModules[idx] && !isLocked && (
                        <div className="px-4 pb-4 space-y-2">
                          {/* Time Progress */}
                          <div className="bg-gray-50 rounded-lg p-3 text-sm">
                            <div className="flex justify-between text-gray-600 mb-1">
                              <span>{t.time_spent}</span>
                              <span>{formatTime(moduleProgress.time_spent_seconds)}</span>
                            </div>
                            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-kamit-gold transition-all"
                                style={{ 
                                  width: `${Math.min(100, (moduleProgress.time_spent_seconds / (3 * 3600)) * 100)}%` 
                                }}
                              />
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{t.time_required}: 3h</p>
                          </div>

                          {/* Lessons */}
                          {module.lessons?.map((lesson, lIdx) => {
                            const isCompleted = moduleProgress.lessons_completed?.includes(lesson.id);
                            return (
                              <button
                                key={lesson.id}
                                onClick={() => setCurrentLesson(lesson)}
                                className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition-colors ${
                                  currentLesson?.id === lesson.id 
                                    ? 'bg-kamit-gold/20 border border-kamit-gold' 
                                    : 'hover:bg-gray-50'
                                }`}
                              >
                                {lesson.content_type === 'video' ? (
                                  <Play className="h-4 w-4 text-kamit-gold" />
                                ) : (
                                  <FileText className="h-4 w-4 text-kamit-gold" />
                                )}
                                <span className="flex-1 text-sm text-kamit-brown truncate">
                                  {getTitle(lesson)}
                                </span>
                                {isCompleted && <Check className="h-4 w-4 text-green-500" />}
                              </button>
                            );
                          })}

                          {/* Quiz Button */}
                          <button
                            onClick={() => quizUnlocked && !moduleProgress.quiz_passed && startQuiz(module.id)}
                            disabled={!quizUnlocked || moduleProgress.quiz_passed}
                            className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                              moduleProgress.quiz_passed
                                ? 'bg-green-50 border-green-200 text-green-700'
                                : quizUnlocked
                                  ? 'bg-kamit-gold/10 border-kamit-gold text-kamit-brown hover:bg-kamit-gold/20'
                                  : 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            {moduleProgress.quiz_passed ? (
                              <>
                                <CheckCircle className="h-5 w-5" />
                                <span className="font-medium">{t.quiz_passed}</span>
                              </>
                            ) : quizUnlocked ? (
                              <>
                                <Award className="h-5 w-5" />
                                <span className="font-medium">{t.start_quiz}</span>
                              </>
                            ) : (
                              <>
                                <Lock className="h-5 w-5" />
                                <span className="font-medium">{t.quiz_locked}</span>
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2">
            {currentLesson ? (
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="p-6 border-b border-gray-100">
                  <h2 className="text-2xl font-heading text-kamit-brown">
                    {getTitle(currentLesson)}
                  </h2>
                </div>

                <div className="p-6">
                  {currentLesson.content_type === 'video' ? (
                    <div className="aspect-video bg-black rounded-lg mb-6">
                      <video
                        src={currentLesson.content_url}
                        controls
                        className="w-full h-full rounded-lg"
                      >
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  ) : (
                    <div 
                      className="prose prose-kamit max-w-none mb-6"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(currentLesson.content || getDescription(currentLesson)) }}
                    />
                  )}

                  <button
                    onClick={() => completeLesson(currentLesson.id)}
                    className="w-full py-3 bg-kamit-gold text-white rounded-lg font-medium hover:bg-kamit-brown transition-colors flex items-center justify-center gap-2"
                  >
                    <Check className="h-5 w-5" />
                    {t.complete_lesson}
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
                <BookOpen className="h-16 w-16 text-kamit-gold/50 mx-auto mb-4" />
                <h2 className="text-2xl font-heading text-kamit-brown mb-2">
                  {language === 'fr' ? 'Sélectionnez une leçon' : 'Select a lesson'}
                </h2>
                <p className="text-gray-600">
                  {language === 'fr' 
                    ? 'Choisissez un module et une leçon dans la liste à gauche pour commencer.'
                    : 'Choose a module and lesson from the list on the left to begin.'
                  }
                </p>
              </div>
            )}

            {/* Completion Section */}
            {progress?.overall_progress === 100 && (
              <div className="mt-8 bg-gradient-to-r from-kamit-gold to-kamit-brown rounded-2xl p-8 text-white text-center">
                <Award className="h-16 w-16 mx-auto mb-4" />
                <h2 className="text-3xl font-heading mb-2">{t.congratulations}</h2>
                <p className="mb-6">{t.course_completed}</p>
                <button
                  onClick={() => navigate(`/certificate/${courseId}`)}
                  className="px-8 py-3 bg-white text-kamit-brown rounded-full font-medium hover:bg-kamit-beige transition-colors"
                >
                  {t.get_certificate}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseViewerPage;
